import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { agentQueries, Persona } from "@/api/personas";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

export function AgentSelector({
  className,
  id,
  value,
  onChange,
}: {
  className?: string;
  id?: string;
  value: Persona["persona_id"];
  onChange: (id: Persona["persona_id"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: agents = [], isLoading: isLoadingPersonas } = useQuery(
    agentQueries.agents(),
  );
  const selectedAgentName = agents.find(
    (agent) => agent.persona_id === value,
  )?.name;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={className}
          disabled={isLoadingPersonas}
        >
          <span className="w-full truncate">
            {selectedAgentName ?? "Select an agent..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command>
          <CommandInput placeholder="Search agents..." />
          <CommandList>
            <CommandEmpty>No agent found.</CommandEmpty>
            <CommandGroup>
              {agents
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((agent) => (
                  <CommandItem
                    key={agent.persona_id}
                    value={agent.persona_id}
                    onSelect={() => {
                      onChange(agent.persona_id);
                      setOpen(false);
                    }}
                    keywords={[agent.name]}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === agent.persona_id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {agent.name}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
